import { supabase } from "../data/supabaseClient";

const REPORTING_HOSTNAME = "refanno.com";
const MAX_TEXT_LENGTH = 4000;
const MAX_MESSAGE_LENGTH = 600;
const CONSOLE_BUFFER_LIMIT = 12;
const TOAST_CORRELATION_WINDOW_MS = 5000;
const REPORTED_STORAGE_PREFIX = "refanno:client-diagnostics:";

let isInstalled = false;
let originalConsoleError = null;
let errorListener = null;
let rejectionListener = null;
let reportingEnabledOverride = null;
let recentConsoleErrors = [];
let reportedFingerprints = new Set();

function getNow() {
  return Date.now();
}

function safeString(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (value instanceof Error) return value.message || value.name || "Error";
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (typeof value === "object" && value.message) {
    return String(value.message);
  }
  try {
    return JSON.stringify(value);
  } catch (_error) {
    return String(value);
  }
}

function sanitizeText(value, limit = MAX_TEXT_LENGTH) {
  const raw = safeString(value);
  if (!raw) return "";
  const sanitized = raw
    .replace(/\bBearer\s+[A-Za-z0-9._~+/=-]+/gi, "Bearer [redacted]")
    .replace(/\b(access_token|refresh_token|token|signature|apikey|api_key)=([^&\s]+)/gi, "$1=[redacted]")
    .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, "[jwt-redacted]")
    .replace(/(https?:\/\/[^\s?#]+)\?([^\s#]+)/gi, "$1?[redacted]");
  return sanitized.length > limit ? sanitized.slice(0, limit) : sanitized;
}

function normalizeError(errorLike) {
  if (errorLike instanceof Error) {
    return {
      name: sanitizeText(errorLike.name || "Error", 120) || "Error",
      message: sanitizeText(errorLike.message || "Unknown error", MAX_MESSAGE_LENGTH),
      stack: sanitizeText(errorLike.stack || "", MAX_TEXT_LENGTH),
      raw: errorLike,
    };
  }

  if (errorLike && typeof errorLike === "object") {
    const name = errorLike.name || errorLike.code || "Error";
    const message = errorLike.message || errorLike.reason || safeString(errorLike);
    return {
      name: sanitizeText(name, 120) || "Error",
      message: sanitizeText(message || "Unknown error", MAX_MESSAGE_LENGTH),
      stack: sanitizeText(errorLike.stack || "", MAX_TEXT_LENGTH),
      raw: errorLike,
    };
  }

  return {
    name: "Error",
    message: sanitizeText(errorLike || "Unknown error", MAX_MESSAGE_LENGTH),
    stack: "",
    raw: errorLike,
  };
}

function getPath() {
  return globalThis.location?.pathname || "/";
}

function getUserAgent() {
  return globalThis.navigator?.userAgent || null;
}

function getTopStackFrame(stack) {
  return String(stack || "")
    .split("\n")
    .map((line) => line.trim())
    .find((line) => line && !line.toLowerCase().startsWith("error")) || "";
}

function hashDjb2(input) {
  let hash = 5381;
  const text = String(input || "");
  for (let i = 0; i < text.length; i += 1) {
    hash = ((hash << 5) + hash) ^ text.charCodeAt(i);
  }
  return (hash >>> 0).toString(16);
}

function buildFingerprint(normalized, context = {}) {
  const source = sanitizeText(context.source || "client", 80).toLowerCase();
  const path = sanitizeText(context.path || getPath(), 240);
  const message = sanitizeText(normalized.message || "", 240).toLowerCase();
  const topFrame = sanitizeText(getTopStackFrame(normalized.stack), 240).toLowerCase();
  return hashDjb2([source, path, message, topFrame].join("|"));
}

function storageKey(fingerprint) {
  return `${REPORTED_STORAGE_PREFIX}${fingerprint}`;
}

function hasReported(fingerprint) {
  if (!fingerprint) return true;
  if (reportedFingerprints.has(fingerprint)) return true;
  try {
    return globalThis.sessionStorage?.getItem(storageKey(fingerprint)) === "1";
  } catch (_error) {
    return false;
  }
}

function markReported(fingerprint) {
  if (!fingerprint) return;
  reportedFingerprints.add(fingerprint);
  try {
    globalThis.sessionStorage?.setItem(storageKey(fingerprint), "1");
  } catch (_error) {
    // Session storage is an optimization. Reporting should still work without it.
  }
}

function hasExtensionStack(text) {
  return /(chrome|moz|safari|edge)-extension:\/\//i.test(String(text || ""));
}

function shouldIgnoreError(normalized, context = {}) {
  const name = String(normalized?.name || "");
  const message = String(normalized?.message || "");
  const stack = String(normalized?.stack || "");
  const source = String(context?.filename || context?.sourceUrl || "");
  const combined = `${name}\n${message}\n${stack}\n${source}`;

  if (name === "AbortError") return true;
  if (/\b(canceled|cancelled)\b/i.test(message)) return true;
  if (/\baborted\b/i.test(message)) return true;
  if (/resizeobserver loop/i.test(message)) return true;
  if (hasExtensionStack(combined)) return true;
  return false;
}

function buildContextLines(context = {}) {
  const lines = [];
  const fields = [
    ["source", context.source],
    ["user_visible_message", context.userMessage],
    ["console_message", context.consoleMessage],
    ["filename", context.filename],
    ["line", context.lineno],
    ["column", context.colno],
  ];

  fields.forEach(([label, value]) => {
    const normalized = sanitizeText(value, 500);
    if (normalized) lines.push(`${label}: ${normalized}`);
  });
  return lines;
}

function buildStackTrace(normalized, context = {}) {
  const lines = [];
  if (normalized.stack) lines.push(normalized.stack);
  const contextLines = buildContextLines(context);
  if (contextLines.length) {
    if (lines.length) lines.push("");
    lines.push("Client diagnostics context:");
    lines.push(...contextLines);
  }
  return sanitizeText(lines.join("\n"), MAX_TEXT_LENGTH) || null;
}

function buildErrorMessage(normalized, context = {}) {
  const errorMessage = sanitizeText(normalized.message || "Unknown error", 300);
  const userMessage = sanitizeText(context.userMessage, 220);
  if (userMessage && userMessage !== errorMessage) {
    return sanitizeText(
      `User-visible error: ${userMessage} | ${normalized.name}: ${errorMessage}`,
      MAX_MESSAGE_LENGTH,
    );
  }
  return sanitizeText(errorMessage, MAX_MESSAGE_LENGTH) || "Unknown error";
}

function isReportingEnabled() {
  if (reportingEnabledOverride !== null) {
    return Boolean(reportingEnabledOverride);
  }
  return globalThis.location?.hostname === REPORTING_HOSTNAME;
}

export async function reportClientError(errorLike, context = {}) {
  const normalized = normalizeError(errorLike);
  if (shouldIgnoreError(normalized, context)) return null;
  if (!isReportingEnabled()) return null;

  const fingerprint = buildFingerprint(normalized, context);
  if (hasReported(fingerprint)) return null;
  markReported(fingerprint);

  const payload = {
    error_message: buildErrorMessage(normalized, context),
    error_name: sanitizeText(normalized.name || "Error", 120) || "Error",
    stack_trace: buildStackTrace(normalized, context),
    component_stack: context.componentStack
      ? sanitizeText(context.componentStack, MAX_TEXT_LENGTH)
      : null,
    path: sanitizeText(context.path || getPath(), 500) || "/",
    user_agent: getUserAgent(),
    occurred_at: new Date().toISOString(),
  };

  try {
    const { error } = await supabase.from("client_error_reports").insert(payload);
    if (error) throw error;
    return payload;
  } catch (error) {
    console.warn("[client-diagnostics] Failed to report client error:", error);
    return null;
  }
}

function extractErrorFromConsoleArgs(args) {
  const list = Array.isArray(args) ? args : [];
  const errorArg = list.find((arg) => arg instanceof Error);
  if (errorArg) return errorArg;

  const messageObject = list.find(
    (arg) => arg && typeof arg === "object" && arg.message,
  );
  if (messageObject) return messageObject;

  const message = list.map((arg) => safeString(arg)).filter(Boolean).join(" ");
  return message ? new Error(message) : new Error("Console error");
}

export function recordConsoleError(args) {
  const errorLike = extractErrorFromConsoleArgs(args);
  const normalized = normalizeError(errorLike);
  if (shouldIgnoreError(normalized, { source: "console.error" })) return null;

  const entry = {
    error: errorLike,
    message: normalized.message,
    stack: normalized.stack,
    path: getPath(),
    occurredAt: getNow(),
  };
  recentConsoleErrors = [...recentConsoleErrors, entry].slice(-CONSOLE_BUFFER_LIMIT);
  return entry;
}

function getRecentConsoleError() {
  const cutoff = getNow() - TOAST_CORRELATION_WINDOW_MS;
  recentConsoleErrors = recentConsoleErrors.filter(
    (entry) => entry.occurredAt >= cutoff,
  );
  return recentConsoleErrors[recentConsoleErrors.length - 1] || null;
}

export function reportRecentErrorToast(message) {
  const entry = getRecentConsoleError();
  if (!entry) return null;
  return reportClientError(entry.error, {
    source: "error_toast",
    path: entry.path,
    userMessage: message,
    consoleMessage: entry.message,
  });
}

export function installClientDiagnostics() {
  if (isInstalled) return;
  isInstalled = true;

  if (globalThis.console?.error && !originalConsoleError) {
    originalConsoleError = globalThis.console.error;
    globalThis.console.error = (...args) => {
      recordConsoleError(args);
      originalConsoleError.apply(globalThis.console, args);
    };
  }

  if (typeof globalThis.addEventListener !== "function") return;

  errorListener = (event) => {
    const error =
      event?.error ||
      new Error(event?.message || "Unhandled browser error");
    void reportClientError(error, {
      source: "window.error",
      filename: event?.filename,
      lineno: event?.lineno,
      colno: event?.colno,
    });
  };

  rejectionListener = (event) => {
    void reportClientError(event?.reason || "Unhandled promise rejection", {
      source: "unhandledrejection",
    });
  };

  globalThis.addEventListener("error", errorListener);
  globalThis.addEventListener("unhandledrejection", rejectionListener);
}

export function resetClientDiagnosticsForTests() {
  if (typeof globalThis.removeEventListener === "function") {
    if (errorListener) globalThis.removeEventListener("error", errorListener);
    if (rejectionListener) {
      globalThis.removeEventListener("unhandledrejection", rejectionListener);
    }
  }
  if (originalConsoleError) {
    globalThis.console.error = originalConsoleError;
  }
  isInstalled = false;
  originalConsoleError = null;
  errorListener = null;
  rejectionListener = null;
  reportingEnabledOverride = null;
  recentConsoleErrors = [];
  reportedFingerprints = new Set();
  try {
    Object.keys(globalThis.sessionStorage || {}).forEach((key) => {
      if (key.startsWith(REPORTED_STORAGE_PREFIX)) {
        globalThis.sessionStorage.removeItem(key);
      }
    });
  } catch (_error) {
    // Tests should not depend on storage availability.
  }
}

export function setClientDiagnosticsReportingEnabledForTests(value) {
  reportingEnabledOverride = value;
}
